<template>
  <div>
    <!-- Formulário de criar/editar -->
    <b-card class="mb-3">
      <validation-observer ref="validator">
        <b-form @submit.prevent="createOrUpdate">
          <b-row>
            <b-col md="4">
              <b-form-group
                :label="$t('reporting_channel.deadline_config.handling_status')"
                label-for="handling_status"
              >
                <v-select
                  v-model="model.handling_status"
                  :options="handlingStatusOptions"
                  :reduce="option => option.key"
                  :get-option-label="option => (option ? option.label : '')"
                  placeholder="Selecione a situação"
                >
                  <template slot="no-options">
                    <span>Nenhuma situação encontrada</span>
                  </template>
                </v-select>
                <small class="text-danger" v-if="errors.handling_status">{{
                  errors.handling_status[0]
                }}</small>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                :label="$t('reporting_channel.deadline_config.deadline_days')"
                label-for="deadline_days"
              >
                <b-form-input
                  id="deadline_days"
                  v-model.number="model.deadline_days"
                  type="number"
                  min="1"
                  :state="errors.deadline_days ? false : null"
                />
                <small class="text-danger" v-if="errors.deadline_days">{{
                  errors.deadline_days[0]
                }}</small>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group
                :label="
                  $t('reporting_channel.deadline_config.is_business_days')
                "
                label-for="is_business_days"
              >
                <b-form-radio-group
                  id="is_business_days"
                  v-model="model.is_business_days"
                  :options="deadlineTypeOptions"
                  name="is_business_days"
                />
                <small class="text-danger" v-if="errors.is_business_days">{{
                  errors.is_business_days[0]
                }}</small>
              </b-form-group>
            </b-col>
          </b-row>
          <div class="d-flex justify-content-end gap-2 mt-3">
            <b-button
              variant="primary"
              @click="createOrUpdate"
              :disabled="!validateForm"
            >
              {{ editingId ? $t('action.save') : $t('action.create') }}
            </b-button>
            <b-button variant="outline-secondary" @click="resetForm">
              {{ $t('action.clear') || 'Limpar' }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </b-card>

    <Grid
      v-if="service && workflowConfigId"
      :service="service"
      route_name="handling_status_configuration_index"
      route_grid_path="grid"
      defaultOrderColumn="id"
      defaultOrderDirection="desc"
      :ref="'grid'"
      :forceFilters="{ workflow_config_id: workflowConfigId }"
      :css_formats="{
        handling_status: (status, model) => {
          if (status && status.color) {
            return `badge badge-glow badge-${status.color}`
          }
          return ''
        }
      }"
      :column_formats="{
        handling_status: val => {
          return val && val.label ? val.label : val || ''
        }
      }"
    >
      <template v-slot:gridThead>
        <vs-th key="id" sort-key="id">ID</vs-th>
        <vs-th key="handling_status_label" sort-key="handling_status">{{
          $t('reporting_channel.deadline_config.handling_status')
        }}</vs-th>
        <vs-th key="deadline_days" sort-key="deadline_days">{{
          $t('reporting_channel.deadline_config.deadline_days')
        }}</vs-th>
        <vs-th key="deadline_type_label" sort-key="is_business_days">{{
          $t('reporting_channel.deadline_config.is_business_days')
        }}</vs-th>
      </template>
      <template v-slot:gridData="props">
        <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in props.data">
          <vs-td key="id">
            <div>{{ tr.id }}</div>
          </vs-td>
          <vs-td key="handling_status_label">
            <div
              :class="getHandlingStatusCss(tr.handling_status)"
              :style="getHandlingStatusStyle(tr.handling_status)"
            >
              {{ getHandlingStatusLabel(tr.handling_status) }}
            </div>
          </vs-td>
          <vs-td key="deadline_days">
            <div>{{ tr.deadline_days }}</div>
          </vs-td>
          <vs-td key="deadline_type_label">
            <div>{{ tr.deadline_type_label }}</div>
          </vs-td>
          <vs-td>
            <slot
              name="actions"
              :row="tr"
              :data="props.data"
              :indextr="indextr"
              :methods="{ edit: props.edit, destroy: props.destroy }"
            >
              <div class="w-full flex gap-2 justify-end">
                <feather-icon
                  v-permission="
                    `reporting_channel_handling.configuration.deadlines.edit`
                  "
                  icon="EditIcon"
                  class="h-5 w-5 mb-1 mr-1 stroke-current text-warning"
                  v-tooltip.top="{
                    content: 'Editar prazo',
                    delay: { show: 100, hide: 100 }
                  }"
                  @click="edit(tr.id)"
                />
                <feather-icon
                  v-permission="
                    `reporting_channel_handling.configuration.deadlines.delete`
                  "
                  icon="Trash2Icon"
                  class="h-5 w-5 mb-1 mr-1 stroke-current text-danger"
                  v-tooltip.top="{
                    content: 'Excluir prazo',
                    delay: { show: 100, hide: 100 }
                  }"
                  @click="destroy(tr.id)"
                />
              </div>
            </slot>
          </vs-td>
        </vs-tr>
      </template>
    </Grid>
  </div>
</template>

<script>
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormRadioGroup,
  BRow,
  BCol,
  BCard
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import ReportingChannelDeadlineConfigService from '@/services/api/reporting-channel/ReportingChannelDeadlineConfigService'
import GenericService from '@/services/api/GenericService'

export default {
  name: 'DeadlineConfigTab',
  directives: {
    Ripple
  },
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormRadioGroup,
    BRow,
    BCol,
    BCard,
    ValidationObserver,
    vSelect
  },
  props: {
    id: {
      type: Number,
      default: null
    },
    workflowConfigId: {
      type: Number,
      default: null
    }
  },
  data: () => ({
    service: null,
    genericService: null,
    editingId: null,
    showForm: false,
    model: {
      handling_status: null,
      deadline_days: null,
      is_business_days: false
    },
    errors: {},
    handlingStatusOptions: [],
    deadlineTypeOptions: [
      { text: 'Dias Úteis', value: true },
      { text: 'Dias Corridos', value: false }
    ]
  }),
  computed: {
    validateForm() {
      return (
        this.model.handling_status &&
        this.model.deadline_days &&
        this.model.deadline_days > 0
      )
    }
  },
  beforeMount() {
    this.service = ReportingChannelDeadlineConfigService.build(this.$vs)
    this.genericService = GenericService.build(this.$vs)
    this.loadHandlingStatus()
  },
  methods: {
    grid() {
      return this.$refs['grid']
    },
    addNew() {
      this.editingId = null
      this.resetForm()
    },
    edit(deadlineId) {
      this.editingId = deadlineId
      this.loadData(deadlineId)
    },
    cancelForm() {
      this.editingId = null
      this.resetForm()
    },
    loadData(id) {
      this.$vs.loading()
      this.service
        .read(id)
        .then(
          response => {
            const handlingStatus =
              typeof response.handling_status === 'object' &&
              response.handling_status !== null
                ? response.handling_status.key
                : response.handling_status

            this.model = {
              handling_status: handlingStatus,
              deadline_days: response.deadline_days,
              is_business_days: response.is_business_days
            }
          },
          error => {
            this.notifyError(this.$vs, 'Não foi possível carregar os dados')
          }
        )
        .finally(() => {
          this.$vs.loading.close()
        })
    },
    loadHandlingStatus() {
      this.genericService.getFormHandlingStatus().then(
        data => {
          this.handlingStatusOptions = data || []
        },
        error => {
          console.error('Erro ao carregar status de tratamento:', error)
          this.handlingStatusOptions = [
            { key: 'OP', label: 'Aberto', color: 'primary' },
            { key: 'IV', label: 'Em validação', color: 'warning' },
            { key: 'IH', label: 'Em tratamento', color: 'warning' },
            { key: 'PE', label: 'Pendente', color: 'warning' },
            { key: 'VA', label: 'Validado', color: 'success' },
            { key: 'CL', label: 'Fechado', color: 'success' }
          ]
        }
      )
    },
    createOrUpdate() {
      this.errors = {}
      this.$vs.loading()

      const handlingStatus =
        typeof this.model.handling_status === 'object' &&
        this.model.handling_status !== null
          ? this.model.handling_status.key
          : this.model.handling_status

      const data = {
        handling_status: handlingStatus,
        deadline_days: this.model.deadline_days,
        is_business_days: this.model.is_business_days
      }

      if (this.workflowConfigId) {
        data.workflow_config_id = this.workflowConfigId
      }

      if (this.editingId) {
        data.id = this.editingId
      }

      const params = this.workflowConfigId
        ? { workflow_config_id: this.workflowConfigId }
        : {}

      const promise = this.editingId
        ? this.service.update(data, params)
        : this.service.create(data, params)

      promise
        .then(
          response => {
            this.notifySuccess(this.$vs, 'Prazo salvo com sucesso!')
            this.editingId = null
            this.resetForm()
            this.grid().fetchGridData()
          },
          error => {
            // O interceptor do axios já trata as notificações de erro
            // Aqui só tratamos erros de validação para mostrar no formulário
            if (
              error.response &&
              error.response.data &&
              error.response.data.errors
            ) {
              this.errors = error.response.data.errors
            }
            // Caso contrário, o interceptor já mostrou a notificação
          }
        )
        .finally(() => {
          this.$vs.loading.close()
        })
    },
    resetForm() {
      this.editingId = null
      this.model = {
        handling_status: null,
        deadline_days: null,
        is_business_days: false
      }
      this.errors = {}
    },
    getHandlingStatusLabel(status) {
      if (status && typeof status === 'object' && status.label) {
        return status.label
      }
      return status || ''
    },
    getHandlingStatusCss(status) {
      if (status && typeof status === 'object' && status.color) {
        const color = status.color
        const isHex = typeof color === 'string' && color.startsWith('#')
        return isHex ? 'badge badge-glow' : `badge badge-glow badge-${color}`
      }
      return ''
    },
    getHandlingStatusStyle(status) {
      if (status && typeof status === 'object' && status.color) {
        const color = status.color
        const isHex = typeof color === 'string' && color.startsWith('#')
        if (isHex) {
          return { backgroundColor: color, color: '#fff' }
        }
      }
      return {}
    },
    destroy(deadlineId) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirmação',
        acceptText: 'Sim',
        cancelText: 'Não',
        text: 'Tem certeza que deseja excluir este prazo?',
        accept: () => {
          this.destroyModel(deadlineId)
        }
      })
    },
    destroyModel(id) {
      this.$vs.loading()
      this.service
        .destroy(id)
        .then(
          response => {
            this.notifySuccess(this.$vs, 'Prazo excluído com sucesso!')
            this.grid().fetchGridData()
          },
          error => {
            this.notifyError(this.$vs, 'Não foi possível excluir o prazo')
          }
        )
        .finally(() => this.$vs.loading.close())
    }
  }
}
</script>

<style scoped></style>
